// src/web/ConnectPage.jsx

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AVIcon from '../../components/icons/AllVoicedIcons';
import NewsletterModal from '../../components/modals/web-modals/NewsletterModal';
import ContactFormModal from '../../components/modals/web-modals/ContactFormModal';
import TranslationIssuesModal from '../../components/modals/web-modals/TranslationIssuesModal';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';

const ConnectPage = () => {
  const { t } = useTranslation();

  const [isNewsletterModalOpen, setNewsletterModalOpen] = useState(false);
  const [isContactFormModalOpen, setContactFormModalOpen] = useState(false);
  const [isTranslationModalOpen, setTranslationModalOpen] = useState(false);
  const ENABLE_TRANSLATIONS = false;

  return (
    <div className="connect-page">
      <PageTracker pageName="ConnectPage" />

      <section className="connect-hero">
        <h1>{t('lets_stay_connected', 'Connect with All-Voiced')}</h1>
        <p>
          {t(
            'connect_intro',
            'Whether you are a clinician, educator, researcher, student, dataset owner, or voice technology team, this is the place to stay in touch, explore services, and start a conversation.'
          )}
        </p>
      </section>

      <section className="connect-section">
        <div className="connect-services-icons">
          <a href="https://linkedin.com/company/allvoiced" target="_blank" rel="noopener noreferrer">
            <AVIcon icon="linkedin" size={28} color="#209385" />
          </a>
          <a href="https://facebook.com/allvoiced" target="_blank" rel="noopener noreferrer">
            <AVIcon icon="facebook" size={28} color="#209385" />
          </a>
          <a href="https://instagram.com/allvoiced" target="_blank" rel="noopener noreferrer">
            <AVIcon icon="instagram" size={28} color="#209385" />
          </a>
          <a href="https://whatsapp.com/channel/0029VbBqyq0ADTOEAeMSI53N" target="_blank" rel="noopener noreferrer">
            <AVIcon icon="whatsapp" size={28} color="#209385" />
          </a>
        </div>

        <h2 className="connect-title">
          {t('stay_in_the_loop', 'Stay in the loop')}
        </h2>

        <p>
          {t(
            'stay_in_the_loop_text',
            'Follow All-Voiced to discover new perceptual skill development tools, research developments, datasets, and opportunities to contribute to the future of voice science and voice AI.'
          )}
        </p>

        <button
          type="button"
          onClick={() => setNewsletterModalOpen(true)}
          className="web-button web-button--primary"
        >
          {t('subscribe_to_newsletter')}
        </button>
      </section>

      <section className="connect-section">
        <div className="connect-services-icons">
          <AVIcon icon="listen" size={28} color="#209385" />
          <AVIcon icon="graduationCap" size={28} color="#209385" />
          <AVIcon icon="research" size={28} color="#209385" />
          <AVIcon icon="tags" size={28} color="#209385" />
        </div>

        <h2 className="connect-title">
          {t('services_connect_title', 'Work with All-Voiced')}
        </h2>

        <p>
          {t(
            'services_connect_text',
            'Explore the All-Voiced service lines and learn how access, institutional implementations, research workflows, and annotation projects are structured.'
          )}
        </p>

        <div className="web-cta-row">
          <Link
            to="/services"
            className="web-button web-button--primary"
          >
            {t('services_connect_cta', 'Explore services')}
          </Link>

          <Link
            to="/access-and-pricing"
            className="web-button web-button--secondary"
          >
            {t(
              'access_and_pricing_cta',
              'Access & Pricing'
            )}
          </Link>
        </div>
      </section>


      <section className="connect-section">
        <div className="connect-icon-top">
          <AVIcon icon="comments" size={28} color="#209385" />
        </div>

        <h2 className="connect-title">
          {t('connect_start_conversation_title', 'Start a conversation')}
        </h2>

        <p>
          {t(
            'connect_start_conversation_text',
            'Tell us about your goals, institutional context, study, dataset, skill-development needs, or voice technology project. Send a message or schedule a meeting, depending on the level of conversation you need.'
          )}
        </p>

        <div className="web-cta-row">
          <button
            type="button"
            onClick={() => setContactFormModalOpen(true)}
            className="web-button web-button--primary"
          >
            {t('send_us_a_message', 'Send us a message')}
          </button>

          <Link
            to="/meet"
            className="web-button web-button--secondary"
          >
            {t('schedule_meeting', 'Schedule a meeting')}
          </Link>
        </div>
      </section>


      {ENABLE_TRANSLATIONS && (
      <section className="connect-section">
        <div className="connect-icon-top">
          <AVIcon icon="languages" size={28} color="#209385" />
        </div>

        <h2 className="connect-title">
          {t('help_improve_translations', 'Help improve translations')}
        </h2>

        <p>
          {t(
            'help_improve_translations_text',
            'All-Voiced is built by and for a global community. If you notice any translation issues or want to contribute in your language, your help is more than welcome!'
          )}
        </p>

        <button
          type="button"
          onClick={() => setTranslationModalOpen(true)}
          className="web-button web-button--primary"
        >
          {t('help_improve_translations', 'Help improve translations')}
        </button>
      </section>
      )}

      <NewsletterModal isOpen={isNewsletterModalOpen} onRequestClose={() => setNewsletterModalOpen(false)} />
      <ContactFormModal isOpen={isContactFormModalOpen} onRequestClose={() => setContactFormModalOpen(false)} />
      <TranslationIssuesModal isOpen={isTranslationModalOpen} onRequestClose={() => setTranslationModalOpen(false)} />

      <ScrollTracker pageName="ConnectPage" />
    </div>
  );
};

export default ConnectPage;
